import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  TextInput,
  Button,
  PinInput,
  Center,
  Box,
  Flex,
  Select,
  Loader,
  Anchor,
} from "@mantine/core";

import { Turnstile } from "@marsidev/react-turnstile";
import { useTheme } from "../contexts/ThemeContext";
import { PrivacyModal } from "./PrivacyModal";

interface PriceAlertModalProps {
  opened: boolean;
  onClose: () => void;
  lowestPrice?: number; // in SEK
}

type Step = "email" | "code" | "success";

// Generate price options based on lowest price
const generatePriceOptions = (lowestPrice: number) => {
  // Round lowest to nearest 250
  const roundedLowest = Math.floor(lowestPrice / 250) * 250;
  // Generate 10 steps above and 10 steps below (21 options total)
  const options = [];
  for (let i = -10; i <= 10; i++) {
    const price = roundedLowest + i * 250;
    if (price < 250) continue;
    options.push({
      value: price.toString(),
      label: price.toLocaleString("sv-SE"),
    });
  }
  return options;
};

export const PriceAlertModal = ({ opened, onClose, lowestPrice = 8000 }: PriceAlertModalProps) => {
  const { gpuCard, theme, currentCard } = useTheme();
  const themeColor = theme === "green" ? "nvidiaGreen" : "amdRed";
  const cardName = currentCard?.display_name || "GPU";
  const [step, setStep] = useState<Step>("email");

  // Default price is 500 below lowest (2 steps), rounded to nearest 250
  const roundedLowest = Math.floor(lowestPrice / 250) * 250;
  const defaultPrice = Math.max(250, roundedLowest - 500);
  const [targetPrice, setTargetPrice] = useState<string>(defaultPrice.toString());
  const priceOptions = generatePriceOptions(lowestPrice);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  // Reset target price when modal opens or lowestPrice changes
  useEffect(() => {
    if (opened) {
      setTargetPrice(defaultPrice.toString());
    }
  }, [opened, defaultPrice]);

  const handleClose = () => {
    setStep("email");
    setTargetPrice(defaultPrice.toString());
    setEmail("");
    setCode("");
    setTurnstileToken("");
    setError("");
    setLoading(false);
    onClose();
  };

  const handleContinueToCode = async () => {
    setLoading(true);
    setError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:2401";
      const response = await fetch(`${apiUrl}/api/alerts/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          target_price: parseInt(targetPrice) * 100, // Convert SEK to öre
          turnstile_token: turnstileToken,
          gpu_model: gpuCard,
        }),
      });

      if (!response.ok) {
        throw new Error("Something went wrong");
      }

      setStep("code");
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:2401";
      const response = await fetch(`${apiUrl}/api/alerts/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
        }),
      });

      if (!response.ok) {
        throw new Error("Something went wrong");
      }

      setStep("success");
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "email":
        return (
          <Stack gap="md">
            <Box>
              <Text size="sm" c="white" fw={500} style={{ lineHeight: 1.8 }}>
                Send an email to{" "}
                <TextInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  type="email"
                  size="sm"
                  className="price-alert-email-input"
                  styles={{
                    input: {
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      borderBottom: "2px dotted rgba(255, 255, 255, 0.3)",
                      borderRadius: 0,
                      background: "transparent",
                      padding: "0 4px",
                      textAlign: "center",
                      display: "inline-block",
                      width: "21ch",
                      maxWidth: "100%",
                      fontSize: "0.875rem",
                    },
                    root: {
                      display: "inline-block",
                    },
                  }}
                />
                <span className="price-alert-line-break"> </span>
                when the price drops to{" "}
                <Select
                  value={targetPrice}
                  onChange={(value) => setTargetPrice(value || "7500")}
                  data={priceOptions}
                  size="sm"
                  searchable
                  className="price-alert-price-input"
                  renderOption={({ option }) => (
                    <Text size="sm">{option.label}</Text>
                  )}
                  rightSection={
                    <Text
                      size="sm"
                      fw={600}
                      c="white"
                      style={{ pointerEvents: "none" }}
                    >
                      kr
                    </Text>
                  }
                  rightSectionWidth={25}
                  withCheckIcon={false}
                  styles={{
                    input: {
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      borderBottom: "2px dotted rgba(255, 255, 255, 0.3)",
                      borderRadius: 0,
                      background: "transparent",
                      padding: "0 4px",
                      paddingRight: "28px",
                      textAlign: "center",
                      display: "inline-block",
                      width: "9ch",
                      maxWidth: "100%",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    },
                    root: {
                      display: "inline-block",
                    },
                  }}
                />{" "}
                or below for the {cardName}?
              </Text>
            </Box>
            <Box mt="lg">
              <Text size="xs" c="dimmed" fw={400} style={{ lineHeight: 1.6 }}>
                In order to setup your alert you have to confirm your email by
                providing a confirmation code sent to it.
              </Text>
            </Box>
            <Center>
              <Turnstile
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ""}
                onSuccess={(token: string) => setTurnstileToken(token)}
                onError={() => setError("CAPTCHA verification failed")}
                onExpire={() => setTurnstileToken("")}
              />
            </Center>
            <Flex justify="space-between" align="center">
              <Anchor
                component="button"
                size="xs"
                c="dimmed"
                onClick={() => setPrivacyModalOpen(true)}
                style={{ textDecoration: "underline" }}
              >
                Privacy Policy
              </Anchor>
              <Flex align="center" gap="md">
                {error && (
                  <Text size="xs" c="red">
                    {error}
                  </Text>
                )}
                <Button
                  variant="light"
                  color={themeColor}
                  size="sm"
                  onClick={handleContinueToCode}
                  loading={loading}
                  disabled={
                    !email || !/\S+@\S+\.\S+/.test(email) || !turnstileToken
                  }
                >
                  Continue
                </Button>
              </Flex>
            </Flex>
          </Stack>
        );

      case "code":
        return (
          <Stack gap="md">
            <Text size="sm" c="white" fw={500} mb="md">
              Please enter the code sent to your email
            </Text>
            <Center mb="md">
              <PinInput
                value={code}
                onChange={setCode}
                length={6}
                size="xl"
                type="number"
                error={!!error}
                placeholder=" "
                className="price-alert-pin-input"
                styles={(theme) => ({
                  input: {
                    fontFamily: "'Courier New', monospace",
                    fontWeight: 700,
                    fontSize: "24px",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                  },
                  root: {
                    gap: "0.25rem",
                  },
                })}
              />
            </Center>
            <Flex justify="flex-end" align="center" gap="md">
              {error && (
                <Text size="xs" c="red">
                  {error}
                </Text>
              )}
              <Button
                variant="light"
                color={themeColor}
                size="sm"
                onClick={handleConfirm}
                loading={loading}
                disabled={code.length !== 6}
              >
                Confirm
              </Button>
            </Flex>
          </Stack>
        );

      case "success":
        return (
          <Stack gap="md" align="center">
            {loading ? (
              <Loader size="lg" color={themeColor} />
            ) : (
              <>
                <Text size="sm" fw={500} c="white" mb="md">
                  You're all set!
                </Text>
                <Button
                  size="sm"
                  onClick={handleClose}
                  variant="light"
                  color={themeColor}
                >
                  Got it!
                </Button>
              </>
            )}
          </Stack>
        );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size="md"
      centered
      padding="xl"
      transitionProps={{
        transition: "fade",
        duration: 150,
        exitDuration: 150,
      }}
      overlayProps={{
        opacity: 0.5,
        blur: 2,
      }}
      styles={{
        content: {
          background: `
            radial-gradient(circle at 20% 30%, var(--theme-overlay-20) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, var(--theme-overlay-15) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, var(--theme-overlay-12) 0%, transparent 70%),
            var(--theme-bg-modal)
          `,
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--theme-border)",
        },
        body: {
          background: "transparent",
        },
      }}
    >
      {renderStep()}

      <PrivacyModal
        opened={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
      />
    </Modal>
  );
};
